import React, {
  FC,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Compass, CompassIcon } from "lucide-react";
import { CalendarIcon } from "@heroicons/react/24/outline";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateRange } from "react-day-picker";
import { Location, AutocompleteResult } from "@/types";
import GlobeComponent from "./globeComponent";
import { debounce } from "lodash";
import api from "@/config/axiosConfig";
import { format, differenceInDays, addDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { generateDaysArray } from "@/utils/tripBuilder";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { useLocationSuggestions } from "@/hooks/useLocationSuggestions";
import { getFlagImage } from "@/lib/general";
import useAuthStore from "@/stores/user/authStore";
import logger from "@/utils/logger";

interface AddDestinationModalProps {
  addLocation: (location: Location) => void;
  handleLocationClick: (location: Location) => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const calculateNights = (dateRange: DateRange | null): number => {
  if (dateRange?.from && dateRange?.to) {
    return differenceInDays(dateRange.to, dateRange.from);
  }
  return 0;
};

type LocationDateRange = {
  location: Location;
  color: string;
};

const locationColors = [
  "#f87171", // bright red
  "#60a5fa", // bright blue
  "#34d399", // bright green
  "#a78bfa", // bright purple
  "#fbbf24", // bright yellow
  "#f472b6", // bright pink
  "#fb923c", // bright orange
  "#22d3ee", // bright cyan
  "#818cf8", // bright indigo
  "#c084fc", // bright violet
];

const calendarStyles = {
  width: "100%",
  "--rdp-cell-size": "32px", // Make cells slightly smaller to accommodate gaps
  "--rdp-accent-color": "#4f46e5",
  "--rdp-background-color": "#f3f4f6",
} as React.CSSProperties;

const AddDestinationModal: FC<AddDestinationModalProps> = ({
  addLocation,
  handleLocationClick,
  isOpen,
  setIsOpen,
}) => {
  const user = useAuthStore((state) => state.user);
  const tripData = useTripStore((state) => state.tripData);
  const lastTripEndDate = useTripStore((state) => state.lastTripEndDate);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [nights, setNights] = useState(0);
  const [inputValue, setInputValue] = useState("");
  const [destination, setDestination] = useState<AutocompleteResult | null>(
    null
  );
  const [autocompleteResults, setAutocompleteResults] = useState<
    AutocompleteResult[]
  >([]);
  const [showTooltip, setShowTooltip] = useState(true);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);

  const disabledDate = useMemo(() => {
    if (!lastTripEndDate) {
      return new Date(); // Default to today if no end date available
    }
    const endDate = new Date(lastTripEndDate);
    return endDate;
  }, [lastTripEndDate]);

  const debouncedFetchPlaces = useCallback((input: string) => {
    setInputValue(input);

    if (input.length <= 2) {
      setAutocompleteResults([]);
      setDestination(null);
      return;
    }

    const fetchPlaces = debounce(async (input: string) => {
      if (!input || input.length <= 2 || input.length > 100) {
        setAutocompleteResults([]);
        setShowTooltip(true);
        return;
      }

      try {
        const response = await api.places.get("/autocomplete", {
          params: {
            q: input,
          },
        });
        const predictions = response.data;
        setShowTooltip(false);
        setAutocompleteResults(predictions);
      } catch (error) {
        logger.error("Error fetching places:", error);
        setShowTooltip(false);
        setAutocompleteResults([]);
      }
    }, 100);
    fetchPlaces(input);
  }, []);

  const handleClose = () => {
    cleanUp();
    setIsOpen(false);
  };

  const handleAutocompleteSelect = async (result: AutocompleteResult) => {
    setDestination(result);
    setInputValue(`${result.name}, ${result.address_components.country}`);
    setAutocompleteResults([]);
    setShowTooltip(true);
  };

  const handleSuggestionSelect = async (suggestion: AutocompleteResult) => {
    setDestination(suggestion);
    setInputValue(
      `${suggestion.name}, ${suggestion.address_components.country}`
    );
    setAutocompleteResults([]);
    setShowTooltip(true);
  };

  const handleAddLocation = () => {
    if (!destination || !dateRange?.from || !dateRange?.to) {
      return;
    }

    const formattedStartDate = formatInTimeZone(
      dateRange.from,
      "UTC",
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z"
    );
    const formattedEndDate = formatInTimeZone(
      dateRange.to,
      "UTC",
      "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
    );

    const newLocation: Location = {
      id: `loc_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      index: 0,
      viator_id: destination.viator_id || 0,
      waytora_id: destination.waytora_id,
      name: destination.name,
      address_components: destination.address_components,
      currency: {
        default: destination.currency.default,
        viator: destination.currency.viator,
      },
      coordinates: {
        latitude: destination.latitude,
        longitude: destination.longitude,
      },
      airports: destination.airports,
      date: {
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        num_days: calculateNights(dateRange) + 1,
        time_zone: destination.timezone,
      },
      itinerary_items: generateDaysArray(formattedStartDate, formattedEndDate),
      origin: false,
      party: {
        adults: 1,
        children: 0,
      },
      budget: {
        total: 0,
        accommodation: 0,
        transport: 0,
        activities: 0,
      },
      accommodation: {
        main: null,
        temp: null,
      },
      participants: [
        {
          id: user?.user_id || "",
          name: user?.name || "",
          profile_image: user?.profile_image || "",
        },
      ],
      image_url: destination.image_url,
    };

    logger.log("newLocation", newLocation);

    addLocation(newLocation);
    handleLocationClick(newLocation);
    cleanUp();
    setIsOpen(false);
  };

  const cleanUp = () => {
    setInputValue("");
    setDateRange(undefined);
    setNights(0);
    setDestination(null);
  };

  const scroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cardWidth = 200;
    const scrollAmount = direction === "left" ? -cardWidth : cardWidth;

    container.scrollBy({
      left: scrollAmount,
      behavior: "smooth",
    });
  };

  const handleDateRangeSelect = (newDateRange: DateRange | undefined) => {
    if (!dateRange?.from) return;

    const endDate = newDateRange?.to || addDays(dateRange.from, 1);

    const utcEndDate = new Date(
      Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    );

    setDateRange({ from: dateRange.from, to: utcEndDate });
    setNights(calculateNights({ from: dateRange.from, to: utcEndDate }));
  };

  const handleDatePopoverOpenChange = (open: boolean) => {
    setIsDatePopoverOpen(open);
    setShowTooltip(!open);
  };

  const isMiddleOfRange = (date: Date) => {
    if (!dateRange?.from || !dateRange?.to) return false;
    return date > dateRange.from && date < dateRange.to;
  };

  const cleanUpAutocompleteResults = () => {
    setAutocompleteResults([]);
  };

  useEffect(() => {
    cleanUpAutocompleteResults();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && lastTripEndDate) {
      const startDate = new Date(lastTripEndDate);
      setDateRange({
        from: startDate,
        to: undefined,
      });
    }
  }, [isOpen, lastTripEndDate]);

  const { data: suggestionData, isLoading: isSuggestionsLoading } =
    useLocationSuggestions(tripData?.name || "", tripData?.locations || []);

  const handlePresetDateRange = (days: number) => {
    if (!lastTripEndDate) return;

    const startDate = new Date(lastTripEndDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + days);

    const utcStartDate = new Date(
      Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate()
      )
    );
    const utcEndDate = new Date(
      Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    );

    setDateRange({ from: utcStartDate, to: utcEndDate });
    setNights(days);
  };

  const existingLocationDates = useMemo(() => {
    if (!tripData?.locations) return [];

    return tripData.locations.map(
      (location, index): LocationDateRange => ({
        location,
        color: locationColors[index % locationColors.length] || "#000",
      })
    );
  }, [tripData?.locations]);

  const modifiers = useMemo(() => {
    const mods: { [key: string]: Date[] } = {};

    existingLocationDates.forEach(({ location }, index) => {
      const startDate = new Date(location.date.start_date);
      const endDate = new Date(location.date.end_date);

      const dates: Date[] = [];
      const currentDate = startDate;

      while (currentDate <= endDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }

      mods[`location-${index}`] = dates;
    });

    return mods;
  }, [existingLocationDates]);

  const modifiersStyles = useMemo(() => {
    const styles: { [key: string]: React.CSSProperties } = {
      rangeMiddle: {
        backgroundColor: "#e0e7ff",
        color: "#3730a3",
      },
    };

    existingLocationDates.forEach(({ color }, index) => {
      styles[`location-${index}`] = {
        backgroundColor: color,
        color: "white",
        opacity: 0.8,
      };
    });

    return styles;
  }, [existingLocationDates]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[1200px] h-[calc(100vh-4rem)] p-0 overflow-hidden bg-white"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex h-full">
          {/* Left Panel - Globe */}
          <div className="w-[40%] relative overflow-hidden bg-neutral-50 border-r border-neutral-200">
            <div className="absolute inset-0 -left-[50%]">
              <GlobeComponent selectedLocation={destination} />
            </div>
          </div>

          {/* Right Panel - Content */}
          <div className="w-[60%] flex flex-col">
            {/* Header */}
            <DialogHeader className="px-6 py-4 border-b border-neutral-200">
              <DialogTitle className="text-2xl font-semibold">
                {tripData?.locations.length === 1
                  ? `Where to next from ${tripData?.locations[0]?.name}?`
                  : "Where to next?"}
              </DialogTitle>
            </DialogHeader>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Search Section */}
              <div className="space-y-2 w-full relative mb-8">
                <Label
                  htmlFor="destination"
                  className="text-sm font-medium text-neutral-800"
                >
                  Where are you going?
                </Label>
                <Input
                  id="destination"
                  value={inputValue}
                  onChange={(e) => debouncedFetchPlaces(e.target.value)}
                  placeholder="Type a city or country..."
                  className="text-sm rounded-lg h-12 focus-visible:ring-0 focus-visible:ring-offset-0 font-light"
                  autoComplete="off"
                />
                {/* Autocomplete Results */}
                {autocompleteResults?.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-xl border border-neutral-100">
                    {autocompleteResults
                      .sort((a, b) => (b.score || 0) - (a.score || 0))
                      .slice(0, 4)
                      .map((result) => (
                        <button
                          key={result.waytora_id}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50"
                          onClick={() => handleAutocompleteSelect(result)}
                        >
                          <div className="flex items-center gap-4">
                            <img
                              src={getFlagImage(
                                result.address_components.country
                              )}
                              alt={result.address_components.country}
                              className="w-5 h-5 rounded-full"
                            />
                            <div className="flex flex-col items-start">
                              <p className="font-medium text-sm">
                                {result.name}
                              </p>
                              <p className="text-xs text-neutral-500 font-light">
                                {result.address_components.country}
                              </p>
                            </div>
                          </div>
                          <i className="fa-solid fa-arrow-right text-indigo-500" />
                        </button>
                      ))}
                  </ul>
                )}
                {/* No Results Message */}
                {autocompleteResults?.length === 0 &&
                  inputValue.length > 3 &&
                  !showTooltip && (
                    <div className="absolute z-10 mt-1 w-full bg-white rounded-xl shadow-xl border border-neutral-100 py-4 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div>
                          <p className="text-sm text-neutral-700 font-medium mb-1">
                            Can&apos;t find &quot;{inputValue}&quot; for you
                            right now.
                          </p>
                          <p className="text-xs text-neutral-500 font-light">
                            Try a different city or country name
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
              </div>

              {/* Suggested Destinations */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-neutral-800">
                  Suggested Destinations
                </h3>
                {suggestionData?.suggestions && (
                  <div className="grid grid-cols-3 gap-2">
                    {suggestionData.suggestions
                      .slice(0, 3)
                      .map((suggestion: AutocompleteResult) => (
                        <div
                          key={suggestion.waytora_id}
                          className="relative group rounded-xl border border-neutral-200 overflow-hidden hover:shadow transition-all duration-300 cursor-pointer hover:border-neutral-300 p-3"
                          onClick={() => handleSuggestionSelect(suggestion)}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-medium text-sm leading-tight mb-1">
                                {suggestion.name}
                              </h3>
                              <p className="text-xs font-light text-neutral-500">
                                {suggestion.address_components.country}
                              </p>
                            </div>
                            <img
                              src={getFlagImage(
                                suggestion.address_components.country
                              )}
                              alt={`${suggestion.address_components.country} flag`}
                              className="w-5 h-5 rounded-full object-cover ring-1 ring-white"
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                )}
                {isSuggestionsLoading && (
                  <div className="grid grid-cols-3 gap-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="relative group rounded-xl border border-neutral-200 overflow-hidden animate-pulse p-4"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="h-4 bg-neutral-100 rounded w-3/4 mb-2" />
                            <div className="h-3 bg-neutral-100 rounded w-1/2" />
                          </div>
                          <div className="w-6 h-6 rounded-full bg-neutral-100" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Date Selection */}
              {destination && (
                <div className="space-y-2 mt-8">
                  <Label className="text-sm font-medium text-neutral-800">
                    What dates are you thinking?
                  </Label>
                  <Popover
                    open={isDatePopoverOpen}
                    onOpenChange={handleDatePopoverOpenChange}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full h-12 rounded-lg justify-start text-left font-light text-sm ${
                          !dateRange && "text-muted-foreground"
                        }`}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange?.from ? (
                          dateRange.to ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} -{" "}
                              {format(dateRange.to, "LLL dd, y")}
                            </>
                          ) : (
                            format(dateRange.from, "LLL dd, y")
                          )
                        ) : (
                          <span className="text-xs font-light">
                            Select dates
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-white relative"
                      align="start"
                    >
                      {/* Location Legend Popup - now tied to isDatePopoverOpen */}
                      {isDatePopoverOpen && (
                        <div className="absolute -top-2 right-0 transform -translate-y-full bg-white rounded-lg shadow-lg border border-neutral-200 p-3 z-20 w-full">
                          <div className="flex flex-row items-center justify-between mb-2">
                            <div className="text-xs font-medium">
                              Trip Schedule
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => scroll("left")}
                                className="p-1 rounded-full border border-neutral-200 transition-all duration-300"
                              >
                                <ChevronLeft className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => scroll("right")}
                                className="p-1 rounded-full border border-neutral-200 transition-all duration-300"
                              >
                                <ChevronRight className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                          <div
                            ref={scrollContainerRef}
                            className="flex gap-2 overflow-x-auto no-scrollbar scroll-smooth"
                            style={{
                              scrollSnapType: "x mandatory",
                              WebkitOverflowScrolling: "touch",
                            }}
                          >
                            {existingLocationDates.map(
                              ({ location, color }, index) => (
                                <div
                                  key={index}
                                  className="flex-none"
                                  style={{ scrollSnapAlign: "start" }}
                                >
                                  <div className="flex flex-row items-center gap-3 p-2 h-full">
                                    <div
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: color }}
                                    ></div>
                                    <div className="flex flex-col">
                                      <span className="font-medium text-sm whitespace-nowrap">
                                        {location.name}
                                      </span>
                                      <span className="text-xs text-neutral-500 font-light whitespace-nowrap">
                                        {format(
                                          new Date(location.date.start_date),
                                          "MMM d"
                                        )}{" "}
                                        -{" "}
                                        {format(
                                          new Date(location.date.end_date),
                                          "MMM d"
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                          <div className="absolute -bottom-2 right-4 w-3 h-3 bg-white border-r border-b border-neutral-200 transform rotate-45" />
                        </div>
                      )}

                      <Calendar
                        initialFocus
                        mode="range"
                        defaultMonth={dateRange?.from}
                        selected={dateRange!}
                        onSelect={handleDateRangeSelect}
                        disabled={(date: Date): boolean => {
                          if (!disabledDate) return true;
                          if (!dateRange?.from) return true;

                          return (
                            date < disabledDate ||
                            date < new Date() ||
                            date < dateRange.from
                          );
                        }}
                        toDate={addDays(dateRange?.from || new Date(), 30)}
                        numberOfMonths={1}
                        modifiers={{
                          ...modifiers,
                          rangeMiddle: isMiddleOfRange,
                          fixedDate: dateRange?.from ? [dateRange.from] : [],
                        }}
                        modifiersStyles={{
                          ...modifiersStyles,
                          fixedDate: {
                            backgroundColor: "#4f46e5",
                            color: "white",
                            fontWeight: "normal",
                          },
                        }}
                        style={calendarStyles}
                        classNames={{
                          months: "flex flex-row space-x-4",
                          month: "space-y-2",
                          caption:
                            "flex justify-center pt-1 relative items-center text-sm",
                          caption_label: "text-sm font-medium",
                          nav: "space-x-1 flex items-center",
                          nav_button:
                            "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                          nav_button_previous: "absolute left-1",
                          nav_button_next: "absolute right-1",
                          table: "w-full border-collapse space-y-1",
                          head_row: "flex justify-between px-1",
                          head_cell:
                            "text-neutral-500 w-9 font-normal text-[0.8rem]",
                          row: "flex w-full mt-2 justify-between px-1",
                          cell: "text-center text-sm p-0 relative hover:bg-neutral-100 rounded-md",
                          day: "h-8 w-8 p-0 font-normal rounded-md flex items-center justify-center mx-[1px]",
                          day_range_end:
                            "rounded-md mx-0 border-1 border-indigo-400",
                          day_selected:
                            "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-md",
                          day_today:
                            "bg-neutral-100 hover:bg-neutral-100 text-neutral-900",
                          day_outside: "text-neutral-400 rounded-md",
                          day_disabled: "text-neutral-400",
                          day_hidden: "invisible",
                          day_range_middle: "rounded-md mx-0 bg-indigo-50",
                          day_range_start:
                            "rounded-md mx-0 bg-indigo-800 text-white hover:bg-indigo-800 hover:text-white",
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  {/* divider */}
                  <div className="w-full flex flex-row items-center justify-center py-2">
                    <span className="text-xs text-neutral-500 font-light">
                      or choose a preset
                    </span>
                  </div>
                  <div className="flex gap-2 mt-4 w-full">
                    {[
                      {
                        days: 3,
                        label: "Short and Sweet",
                        icon: "fa-regular fa-clock",
                      },
                      {
                        days: 5,
                        label: "Take it Easy",
                        icon: "fa-regular fa-clock",
                      },
                      {
                        days: 7,
                        label: "Week in Paradise",
                        icon: "fa-regular fa-clock",
                      },
                    ].map(({ days, label, icon }) => (
                      <div
                        key={days}
                        onClick={() => handlePresetDateRange(days)}
                        className="text-xs ring-1 ring-neutral-200 hover:ring-indigo-300 py-3 flex-1 rounded-lg transition-all duration-300 group cursor-pointer"
                      >
                        <div className="flex flex-col items-center space-y-1">
                          <span className="font-medium">{label}</span>
                          <span className="text-neutral-500 font-light">
                            {format(dateRange?.from || new Date(), "MMM d")} -{" "}
                            {format(
                              addDays(dateRange?.from || new Date(), days),
                              "MMM d"
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200 p-6">
              <Button
                className="gap-3 w-full h-12 rounded-xl text-base ring-1 ring-inset ring-neutral-200 bg-white text-neutral-800 font-medium hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                onClick={handleAddLocation}
                disabled={!destination || !dateRange?.from || !dateRange?.to}
              >
                {destination
                  ? `Add ${destination.name}`
                  : "Select a destination"}
                {destination && (
                  <img
                    src={getFlagImage(destination.address_components.country)}
                    alt={`${destination.address_components.country} flag`}
                    className="w-5 h-5 rounded-full shadow-sm"
                  />
                )}
              </Button>
              <p className="text-center text-sm text-neutral-500 font-light mt-3">
                {destination && nights > 0
                  ? `${nights} nights in ${destination.name}`
                  : ""}
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddDestinationModal;
